import type { ClientDetails, PortalState, ReportSection, ReportVersion } from "./domain";
import { approvalSnapshotMatches, clientPortalPreviewReviewIsCurrent, reportTextWarnings } from "./report-approval";
import { resolveClientReportContentStates } from "./report-content-state";
import { deriveSectionProgressResult, type SectionProgressResult } from "./report-section-progress";
import { sectionOneReadiness } from "./section1-interpretation";
import { sectionTwoReadiness } from "./section2-interpretation";
import { sectionThreeReadiness } from "./section3-interpretation";
import { refinedSectionFourReadiness } from "./pages/RefinedSectionFourWorkflow";
import { sectionFiveReadiness, sectionSixReadiness } from "./pages/SectionFiveSixWorkflow";
import { sectionEightReadiness, sectionNineReadiness, sectionSevenReadiness } from "./pages/SectionSevenNineWorkflow";
import { sectionTenReadiness } from "./pages/SectionTenWorkflow";
import { resolveClientPresentation } from "./client-presentation";

export type NativeReadiness = { ready: boolean; missing: string[] };

export interface ReportNativeReadiness {
  readinessById: Record<string, NativeReadiness>;
  progressById: Record<string, SectionProgressResult>;
  sourceProgressById: Record<string, SectionProgressResult>;
  eligibleSourceIds: Set<string>;
  navigationStatuses: Record<string, SectionProgressResult["label"]>;
  completedSectionCount: number;
}

function withClientFormattingReadiness(section:ReportSection,readiness:NativeReadiness):NativeReadiness{
  const presentation=resolveClientPresentation(section);
  if(presentation.resolution!=="Formatting needs review")return readiness;
  return{ready:false,missing:[...readiness.missing,"review the current client formatting"]};
}

export function evaluateReportNativeReadiness(
  version: ReportVersion,
  state: PortalState,
  auditEngagementId: string | undefined,
  details: ClientDetails | undefined,
  hostedAuthority = false,
): ReportNativeReadiness {
  const byId = Object.fromEntries(version.sections.map(section => [section.id, section])) as Record<string, ReportSection>;
  const sourceReadiness = (section: ReportSection): NativeReadiness => {
    if (section.id === "google-understanding") return sectionOneReadiness(section);
    if (section.id === "maps-profile") return sectionTwoReadiness(section);
    if (section.id === "website-review") return sectionThreeReadiness(section);
    if (section.id === "prompt-testing") return refinedSectionFourReadiness(section, state, auditEngagementId);
    if (section.id === "overall-findings") return sectionFiveReadiness(section, byId["prompt-testing"], state, auditEngagementId);
    if (section.id === "commercial-gaps") return sectionSixReadiness(section, byId["prompt-testing"], byId["overall-findings"], details);
    if (section.id === "competitors") return sectionSevenReadiness(section, state, auditEngagementId, byId["prompt-testing"]);
    if (section.id === "authority-gaps") return sectionEightReadiness(section, state, auditEngagementId);
    return sectionNineReadiness(
      section,
      byId["prompt-testing"],
      byId["overall-findings"],
      byId["commercial-gaps"],
      details,
      {
        sectionOne:byId["google-understanding"],
        sectionTwo:byId["maps-profile"],
        sectionThree:byId["website-review"],
        sectionEight:byId["authority-gaps"],
      },
    );
  };
  const sourceSections = version.sections.filter(section => section.id !== "work-programme");
  const sourceReadinessById = Object.fromEntries(sourceSections.map(section => {
    let readiness=sourceReadiness(section);
    if(hostedAuthority&&["google-understanding","maps-profile","website-review"].includes(section.id)&&!(section.hostedSourceReferences?.length)) {
      readiness={ready:false,missing:[...readiness.missing,"at least one hosted source reference"]};
    }
    return[section.id,withClientFormattingReadiness(section,readiness)];
  })) as Record<string, NativeReadiness>;
  const sourceProgressById = Object.fromEntries(
    sourceSections.map(section => [section.id, deriveSectionProgressResult(section, sourceReadinessById[section.id])]),
  ) as Record<string, SectionProgressResult>;
  const eligibleSourceIds = new Set(
    Object.entries(sourceProgressById).filter(([, value]) => value.eligibleForSectionTen).map(([sectionId]) => sectionId),
  );
  const readinessById = {
    ...sourceReadinessById,
    ...Object.fromEntries(
      version.sections
        .filter(section => section.id === "work-programme")
        .map(section => [section.id, withClientFormattingReadiness(section,sectionTenReadiness(section, version.sections, details, eligibleSourceIds))]),
    ),
  } as Record<string, NativeReadiness>;
  const progressById = Object.fromEntries(
    version.sections.map(section => [section.id, deriveSectionProgressResult(section, readinessById[section.id])]),
  ) as Record<string, SectionProgressResult>;
  return {
    readinessById,
    progressById,
    sourceProgressById,
    eligibleSourceIds,
    navigationStatuses:Object.fromEntries(version.sections.map(section => [
      section.id,
      resolveClientPresentation(section).resolution === "Formatting needs review"
        ? "Formatting needs review"
        : progressById[section.id].label,
    ])),
    completedSectionCount:version.sections.filter(section => progressById[section.id].complete).length,
  };
}

export function completeReportBlockers(
  version: ReportVersion,
  native: ReportNativeReadiness,
  persistenceStatus: string,
) {
  return reportReviewPreparationBlockers(version,native,persistenceStatus,true);
}

export function reportReviewPreparationBlockers(
  version:ReportVersion,
  native:ReportNativeReadiness,
  persistenceStatus:string,
  requireCurrentReview=false,
) {
  const blockers:string[] = [];
  const contentStates = resolveClientReportContentStates(version.sections, native.readinessById);
  for (const section of version.sections) {
    const readiness = native.readinessById[section.id];
    if (!readiness.ready) blockers.push(`Section ${section.order}: ${readiness.missing.join(", ")}`);
    if (!native.progressById[section.id].complete) blockers.push(`Section ${section.order}: complete the section`);
    if (!contentStates[section.id].currentForApproval) {
      blockers.push(`Section ${section.order}: ${contentStates[section.id].reason}`);
    }
  }
  if (requireCurrentReview&&!clientPortalPreviewReviewIsCurrent(version)) {
    blockers.push("Mark this version reviewed in the client portal preview; any later client-visible edit invalidates the review");
  }
  if (persistenceStatus !== "Saved locally"&&persistenceStatus !== "Saved and confirmed by Supabase") blockers.push("Wait for the report draft to be confirmed by its authoritative save source");
  blockers.push(...reportTextWarnings(version));
  return [...new Set(blockers)];
}

export function reportApprovalAndPublicationReadiness(
  version:ReportVersion,
  native:ReportNativeReadiness,
  persistenceStatus:string,
) {
  const approvalBlockers = completeReportBlockers(version, native, persistenceStatus);
  const approvalReady = (version.status === "Draft" || version.status === "Under review") && approvalBlockers.length === 0;
  const publicationBlockers = [
    ...(version.status === "Approved" ? [] : ["The report version is not approved"]),
    ...(!version.approvalSnapshot || !approvalSnapshotMatches(version, version.approvalSnapshot) ? ["The exact approval snapshot is missing or no longer matches"] : []),
  ];
  return {
    approvalReady,
    approvalBlockers,
    publicationReady:publicationBlockers.length === 0,
    publicationBlockers,
  };
}
